import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import useApi from "../hook/useApi";
import Loader from "./Loader/Loader";
import { rewardValidation, ValidateInputs } from "../utils/helper";

const RewardLayer = () => {
  const { AsyncGetApiCall, AsyncPatchAPICall } = useApi();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    oneTokenValue: "",
    data: [],
  });

  const [data, setData] = useState({
    oneTokenValue: "",
    data: [], // <-- TIER ARRAY
  });

  // --------------------------
  // Fetch Config
  // --------------------------
  const getRewardConfig = async () => {
    setLoading(true);
    const result = await AsyncGetApiCall(`/flytoken/get-config`);

    if (result?.success && result?.data) {
      setData({
        oneTokenValue: result?.data?.oneTokenValue || "",
        data: result?.data?.data || [],
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    getRewardConfig();
  }, []);

  // --------------------------
  // Handle Change For Main Fields
  // --------------------------

  const handleValues = (e) => {
    const { name, value } = e.target;

    // Clamp the value to 0.0001 - 5
    let newValue = value === "" ? "" : value;

    // Update data state with clamped value
    setData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validate and update errors
    const updatedInputs = ValidateInputs(name, newValue, errors); // Use clamped value for validation
    setErrors((prev) => ({ ...prev, ...updatedInputs }));
  };

  // --------------------------
  // Handle Tier Value Change
  // --------------------------
  const handleTierChange = (index, field, value) => {
    const updated = [...data.data];
    updated[index][field] = value;

    setData((prev) => ({
      ...prev,
      data: updated,
    }));

    // Validate and update errors for this specific tier field
    const updatedInputs = rewardValidation(field, value);
    setErrors((prev) => {
      const newDataErrors = [...(prev.data || [])];
      newDataErrors[index] = {
        ...(newDataErrors[index] || {}),
        ...updatedInputs,
      };
      return { ...prev, data: newDataErrors };
    });
  };

  // --------------------------
  // Add New Tier Row
  // --------------------------
  const addTierRow = () => {
    setData((prev) => ({
      ...prev,
      data: [...prev.data, { tierLevelName: "", pointPerURO: "" }],
    }));
  };

  // --------------------------
  // Delete Tier Row
  // --------------------------
  const deleteTierRow = (index) => {
    const updated = data.data.filter((_, i) => i !== index);
    setData((prev) => ({
      ...prev,
      data: updated,
    }));
  };

  // --------------------------
  // Save Config
  // --------------------------
  const updateReward = async (e) => {
    e.preventDefault();

    if (!data.oneTokenValue || Number(data.oneTokenValue) <= 0) {
      toast.error("One Token Value is required.");
      return;
    }

    // 2. Validate Tier Array Values
    // --------------------------
    for (let i = 0; i < data.data.length; i++) {
      const tier = data.data[i];

      if (!tier.tierLevelName?.trim()) {
        toast.error(`Tier ${i + 1}: Tier Level Name is required.`);
        return;
      }

      if (!tier.pointPerURO) {
        toast.error(`Tier ${i + 1}: Points Per EURO is required.`);
        return;
      }

      const points = Number(tier.pointPerURO);

      // Range validation
      if (points < 0.0001 || points > 5) {
        toast.error(
          `Tier ${i + 1}: Points Per EURO must be between 0.0001 and 5.`
        );
        return;
      }
    }

    try {
      setLoading(true);

      const result = await AsyncPatchAPICall(`/flytoken/create-config`, data);

      if (result.errors) {
        toast.error(result.errors[0]);
      } else {
        toast.success("Rewards configuration updated.");
        getRewardConfig();
      }
    } catch (error) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader />}

      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Fly Token Configuration</h5>
          </div>

          <div className="card-body">
            <form onSubmit={updateReward}>
              {/* ONE TOKEN VALUE */}
              <label className="form-label">One Token Value (In Eur)</label>
              <div className="d-flex flex-column salign-items-center gap-2 mb-3">
                <input
                  type="number"
                  className="form-control"
                  name="oneTokenValue"
                  value={data?.oneTokenValue}
                  onChange={handleValues}
                  placeholder="Enter one token value"
                  onKeyDown={(e) => {
                    // Prevent typing negative numbers or 'e' (exponential)
                    if (["e", "E", "+", "-"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors.oneTokenValue && (
                  <div className="invalid-feedback d-block">{errors.oneTokenValue}</div>
                )}
              </div>

              <hr />

              <br />

              {/* TIER TABLE */}
              <h6 className="mb-2">Rewards Level</h6>

              <br />

              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th style={{ width: "40%" }}>Tier Level Name</th>
                    <th style={{ width: "40%" }}>Points Per EURO</th>
                    {/* <th style={{ width: "10%" }}>Action</th> */}
                  </tr>
                </thead>

                <tbody>
                  {data.data.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <input
                          className="form-control"
                          value={row?.tierLevelName}
                          onChange={(e) =>
                            handleTierChange(
                              index,
                              "tierLevelName",
                              e.target.value
                            )
                          }
                          placeholder="Enter tier name"
                        />
                        {errors.data?.[index]?.tierLevelName && (
                          <div className="invalid-feedback d-block">
                            {errors.data[index].tierLevelName}
                          </div>
                        )}
                      </td>

                      <td>
                        <input
                          type="number"
                          className="form-control"
                          value={row?.pointPerURO}
                          onChange={(e) =>
                            handleTierChange(
                              index,
                              "pointPerURO",
                              e.target.value
                            )
                          }
                          placeholder="Enter points"
                        />
                        {errors.data?.[index]?.pointPerURO && (
                          <div className="invalid-feedback d-block">
                            {errors.data[index].pointPerURO}
                          </div>
                        )}
                      </td>

                      {/* <td className="text-center">
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => deleteTierRow(index)}
                        >
                          Delete
                        </button>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ADD ROW BUTTON */}
              {/* <button
                type="button"
                onClick={addTierRow}
                className="btn btn-secondary mb-3"
              >
                + Add New Tier
              </button> */}

              <br />

              {/* SAVE BUTTON */}
              <button type="submit" className="btn btn-primary">
                Update Configuration
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default RewardLayer;
